/**
 * Analytic FPS walker — capsule-vs-AABB/cylinder horizontal resolution,
 * walkable collider tops (stairs / wall-walk / battlements) with step-up,
 * gravity + jump. Ground support = max(terrain, collider top).
 */
import * as THREE from 'three';

export const EYE_HEIGHT = 1.68;
const RADIUS = 0.36;
const STEP_UP = 0.5;
const GRAVITY = 22;

export interface WalkerInput {
  forward: number;
  strafe: number;
  jump: boolean;
  sprint: boolean;
}

export interface WalkerCollider {
  kind: 'aabb' | 'cylinder' | 'ring';
  minX?: number;
  maxX?: number;
  minZ?: number;
  maxZ?: number;
  x?: number;
  z?: number;
  r?: number;
  /** ring: annulus centred (x,z) between rIn..rOut (walkable on top).
   *  aStart/aEnd: solid angular span in degrees (0 = +Z/south, -180..180). */
  rIn?: number;
  rOut?: number;
  aStart?: number;
  aEnd?: number;
  top: number;
  bottom: number;
}

export class Walker {
  pos = new THREE.Vector3();
  yaw = Math.PI;
  pitch = -0.05;
  vy = 0;
  grounded = true;
  speedWalk = 4.4;
  speedRun = 8.2;

  private groundH: (x: number, z: number) => number;

  constructor(
    groundHeightFn: (x: number, z: number) => number,
    x: number,
    z: number,
  ) {
    this.groundH = groundHeightFn;
    this.pos.set(x, this.groundH(x, z) + EYE_HEIGHT, z);
  }

  look(dx: number, dy: number): void {
    this.yaw -= dx * 0.0023;
    this.pitch = Math.max(-1.45, Math.min(1.45, this.pitch - dy * 0.0023));
  }

  teleport(x: number, z: number, yawDeg?: number, y?: number): void {
    this.pos.set(x, y != null ? y : this.groundH(x, z) + EYE_HEIGHT + 0.06, z);
    this.vy = 0;
    if (yawDeg != null) this.yaw = (yawDeg * Math.PI) / 180;
  }

  get feetY(): number {
    return this.pos.y - EYE_HEIGHT;
  }

  private blocks(c: WalkerCollider, feet: number, head: number): boolean {
    if (feet >= c.top - STEP_UP) return false; // low enough to step onto
    if (head <= c.bottom + 0.02) return false; // passing underneath
    return true;
  }

  private resolveXZ(px: number, pz: number, feet: number, head: number): [number, number] {
    for (let iter = 0; iter < 2; iter += 1) {
      let moved = false;
      for (const c of this.colliders) {
        if (!this.blocks(c, feet, head)) continue;
        if (c.kind === 'aabb') {
          const cx = Math.max(c.minX ?? 0, Math.min(px, c.maxX ?? 0));
          const cz = Math.max(c.minZ ?? 0, Math.min(pz, c.maxZ ?? 0));
          const dx = px - cx;
          const dz = pz - cz;
          const d2 = dx * dx + dz * dz;
          if (d2 > RADIUS * RADIUS) continue;
          if (d2 > 1e-9) {
            // Push radially away from the closest point.
            const d = Math.sqrt(d2);
            px = cx + (dx / d) * (RADIUS + 0.001);
            pz = cz + (dz / d) * (RADIUS + 0.001);
          } else {
            // Centre inside the rect: exit along the shallowest axis.
            const l = px - (c.minX ?? 0);
            const rr = (c.maxX ?? 0) - px;
            const tt = pz - (c.minZ ?? 0);
            const bb = (c.maxZ ?? 0) - pz;
            const m = Math.min(l, rr, tt, bb);
            if (m === l) px = (c.minX ?? 0) - RADIUS - 0.001;
            else if (m === rr) px = (c.maxX ?? 0) + RADIUS + 0.001;
            else if (m === tt) pz = (c.minZ ?? 0) - RADIUS - 0.001;
            else pz = (c.maxZ ?? 0) + RADIUS + 0.001;
          }
          moved = true;
        } else if (c.kind === 'ring') {
          const dx = px - (c.x ?? 0);
          const dz = pz - (c.z ?? 0);
          const d = Math.hypot(dx, dz) || 0.0001;
          const rIn = c.rIn ?? 0;
          const rOut = c.rOut ?? 1;
          // Inside the solid annulus band (with capsule radius padding)?
          if (d > rIn - RADIUS && d < rOut + RADIUS) {
            // Walkable top: step onto it instead of blocking.
            if (feet >= c.top - STEP_UP) continue;
            const deg = (Math.atan2(dx, dz) * 180) / Math.PI;
            if (deg < (c.aStart ?? -180) || deg > (c.aEnd ?? 180)) continue; // gate span
            const toInner = Math.abs(d - rIn);
            const toOuter = Math.abs(d - rOut);
            const target = toInner < toOuter ? rIn - RADIUS - 0.001 : rOut + RADIUS + 0.001;
            px = (c.x ?? 0) + (dx / d) * target;
            pz = (c.z ?? 0) + (dz / d) * target;
            moved = true;
          }
        } else {
          const dx = px - (c.x ?? 0);
          const dz = pz - (c.z ?? 0);
          const d2 = dx * dx + dz * dz;
          const rr = RADIUS + (c.r ?? 1);
          if (d2 >= rr * rr) continue;
          const d = Math.sqrt(d2) || 0.0001;
          px = (c.x ?? 0) + (dx / d) * (rr + 0.001);
          pz = (c.z ?? 0) + (dz / d) * (rr + 0.001);
          moved = true;
        }
      }
      if (!moved) break;
    }
    return [px, pz];
  }

  colliders: WalkerCollider[] = [];

  update(dt: number, input: WalkerInput): void {
    const sinY = Math.sin(this.yaw);
    const cosY = Math.cos(this.yaw);
    // yaw=PI faces -Z (toward the castle from the south road).
    const fwdX = -sinY;
    const fwdZ = -cosY;
    const rightX = cosY;
    const rightZ = -sinY;
    let mx = fwdX * input.forward + rightX * input.strafe;
    let mz = fwdZ * input.forward + rightZ * input.strafe;
    const len = Math.hypot(mx, mz);
    if (len > 1e-4) {
      mx /= len;
      mz /= len;
    }
    const speed = input.sprint ? this.speedRun : this.speedWalk;

    const head = this.pos.y + 0.25;
    const feet = this.feetY;
    const nx = this.pos.x + mx * speed * dt;
    const nz = this.pos.z + mz * speed * dt;
    const [rx, rz] = this.resolveXZ(nx, nz, feet, head);
    this.pos.x = rx;
    this.pos.z = rz;

    // Support: terrain plus any collider top we stand on.
    let support = this.groundH(this.pos.x, this.pos.z);
    for (const c of this.colliders) {
      let inside = false;
      if (c.kind === 'aabb') {
        inside =
          this.pos.x > (c.minX ?? 0) - RADIUS * 0.55
          && this.pos.x < (c.maxX ?? 0) + RADIUS * 0.55
          && this.pos.z > (c.minZ ?? 0) - RADIUS * 0.55
          && this.pos.z < (c.maxZ ?? 0) + RADIUS * 0.55;
      } else if (c.kind === 'ring') {
        const dxc = this.pos.x - (c.x ?? 0);
        const dzc = this.pos.z - (c.z ?? 0);
        const dc = Math.hypot(dxc, dzc);
        inside = dc > (c.rIn ?? 0) - RADIUS * 0.55 && dc < (c.rOut ?? 1) + RADIUS * 0.55;
        if (inside) {
          const degS = (Math.atan2(dxc, dzc) * 180) / Math.PI;
          if (degS < (c.aStart ?? -180) || degS > (c.aEnd ?? 180)) inside = false;
        }
      } else {
        const dx = this.pos.x - (c.x ?? 0);
        const dz = this.pos.z - (c.z ?? 0);
        inside = dx * dx + dz * dz < ((c.r ?? 1) + RADIUS * 0.55) ** 2;
      }
      if (inside && c.top <= this.feetY + STEP_UP && c.top > support && this.feetY > c.bottom - 0.01) {
        support = c.top;
      }
    }

    this.vy -= GRAVITY * dt;
    if (input.jump && this.grounded) {
      this.vy = 7.6;
      this.grounded = false;
    }
    this.pos.y += this.vy * dt;
    if (this.pos.y - EYE_HEIGHT <= support) {
      this.pos.y = support + EYE_HEIGHT;
      this.vy = Math.max(0, this.vy);
      this.grounded = true;
    } else if (this.vy < -0.5) {
      this.grounded = false;
    }
  }
}

